import React, { useCallback, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { connect } from "react-redux";
import { actions } from "features/Model/Enclosure/_ducks";
import { createStructuredSelector } from "reselect";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getBaseUnits, getOptions, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";
import { useSubcomponentDirectionLocked } from "features/Model/Enclosure/_utils/useEffectiveFacingDirection";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Tooltip from "components/Tooltip";
import Select from "components/Input/Select";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";
import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

const { setInitCode, updateCodeDrawer } = actions;
const floorHeaderTypeValidation = [required, ...getValidation("floorHeaderInsType")];
const orientationValidation = getValidation("floorHeaderDirection");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Floor Header Ins. Type codes found in code library
export const floorHeaderTypeLibCodesMap = (libCodes, handleEditLibFloorHeaderCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("FloorHeader-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibFloorHeaderCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibFloorHeaderCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Floor Header Ins. Type codes found in model, filtered by those in library
export const floorheaderTypeModelCodesMap = (modelCodes, libCodes, handleEditModelFloorHeaderCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef = "" }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "FloorHeader") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "FloorHeader";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("FloorHeader-U");

            return {
                label,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelFloorHeaderCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const Construction = React.memo(
    ({
        accessor,
        change,
        id,
        setInitCode,
        updateCodeDrawer,
        disabledCodeEdit = false,
        canBeEditedFromParametric = true,
        isCompact = false,
        parentAccessor = "",
        selectedUpgrade,
        isCommunity = false,
        isParametricAnalysis = false,
        codeRefs = [],
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const { FloorHeader: { codes: libCodes = {} } = {} } = useStateSelector(`user.codeLib`) || {};
        const { calculatingRValue = {} } = useStateSelector(`enclosure`) || {};
        const { currentPackage = {} } = useStateSelector(`upgradeLibrary`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { community = {} } = useStateSelector(`community`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key])
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const programMode = modelData?.program?.class || "";

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.floorHeaderInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.floorHeaderInsType_nomRVal`) || 0;
        const floorHeaderCodeWarning = useStateSelector(`${accessor}.floorHeaderInsType_warning`) || "";
        const rValUnits = useStateSelector(`${accessor}.floorHeaderInsType_u`);
        const { [id]: { floorHeaderInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified" || isTouched;

        const { id: floorHeaderFacingId } = useStateSelector(`${accessor}.facingDirection`) || {};
        const { id: parentFacingId } = useStateSelector(`${parentAccessor}.facingDirection`) || {};

        const isErsAndUSpec =
            selectedCodeRef === "UserSpecified" && (programMode.includes("ERS") || programMode.includes("ONrh"));

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.floorHeaderInsType_warning`, "");
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.floorHeaderInsType_nomRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.floorHeaderInsType_effRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.floorHeaderInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libCodes[codeRef] || {};
                    change(`${accessor}.floorHeaderInsType_nomRVal`, nominalRValue || 0.1);
                    change(`${accessor}.floorHeaderInsType_effRVal`, nominalRValue || 0.1);
                    change(`${accessor}.floorHeaderInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                    }
                }
                //Redundancy
                change(`model.stashedCodes.FloorHeader.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleEditModelFloorHeaderCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "floorHeaderInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Floor Header",
                    isEditing: true,
                    componentType: "FloorHeader",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.floorHeaderInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibFloorHeaderCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef, codeType) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const initCodeType = codeType === "U" ? "udefStandard" : "floorHeaderInsType";
                    const code = { ...libCodes[codeRef], codeRef: codeRef, isLibCode: true };
                    await setInitCode(code, initCodeType);
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Floor Header",
                        isEditing: true,
                        componentType: "FloorHeader",
                        codeType: codeType,
                        fieldAccessor: `${accessor}.floorHeaderInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libCodes, setInitCode, updateCodeDrawer]
            );

        const isDisableDir = useSubcomponentDirectionLocked("floorHeader", accessor, parentAccessor);

        return (
            <>
                <InputRow
                    gridTemplate={isCompact ? "1fr 1fr 1fr" : "31.85% 23.25%"}
                    style={{ marginBottom: isCompact ? "0rem" : "1.5rem" }}
                    compact={isCompact}
                >
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.floorHeaderInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...floorheaderTypeModelCodesMap(modelCodes, libCodes, handleEditModelFloorHeaderCode),
                            ...floorHeaderTypeLibCodesMap(libCodes, handleEditLibFloorHeaderCode),
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Floor Header Type"
                        placeholder="Choose Floor Header Type"
                        searchPlaceholder="Search Floor Header Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Floor Header",
                                        isEditing: false,
                                        componentType: "FloorHeader",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.floorHeaderInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="floorHeaderInsType"
                            />
                        )}
                        className={classes.floorHeaderType}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    {selectedCodeRef === "UserSpecified" ? (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.floorHeaderInsType_effRVal`}
                            label={"Effective R-Value"}
                            placeholder={0.0}
                            disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                            validate={floorHeaderTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("floorHeaderInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1} // selectedNomRValue is updated to equal floorHeaderInsType_effRVal
                            units={{
                                base: getBaseUnits("floorHeaderInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.floorHeaderInsType_u`,
                            }}
                            onChange={(value) => {
                                if (selectedCodeRef === "UserSpecified") {
                                    change(`${accessor}.floorHeaderInsType_nomRVal`, value);
                                }
                            }}
                            info={"User specified values are assumed to be effective R-values."}
                            isLoading={isCalculatingRValue}
                            warning={isErsAndUSpec ? "ersUSpecRVal" : ""}
                            compact={isCompact}
                        />
                    ) : (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.floorHeaderInsType_nomRVal`}
                            label={"Nominal R-Value"}
                            placeholder={0.0}
                            disabled={true}
                            validate={floorHeaderTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("floorHeaderInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1}
                            units={{
                                base: getBaseUnits("floorHeaderInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.floorHeaderInsType_u`,
                            }}
                            info={
                                "Values calculated by the Code useStateSelector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                            }
                            warning={floorHeaderCodeWarning}
                            isLoading={isCalculatingRValue}
                            compact={isCompact}
                        />
                    )}
                    {isCompact && (
                        <Field
                            component={Select}
                            name={`${accessor}.facingDirection`}
                            options={getOptions({
                                fieldKey: "floorHeaderDirection",
                            }).map((option) => ({
                                ...option,
                                disabled: codeRefs.includes(option.value?.codeRef),
                            }))}
                            label="Facing Direction"
                            placeholder="Choose Facing Direction"
                            validate={orientationValidation}
                            disabled={isDisableDir}
                            compact
                            compassProps={{
                                facingId: floorHeaderFacingId,
                                disabled: isDisableDir,
                                rotationOptions: {
                                    0: 0,
                                    1: 180,
                                    2: 135,
                                    3: 90,
                                    4: 45,
                                    5: 0,
                                    6: -45,
                                    7: -90,
                                    8: -135,
                                    9: 0,
                                },
                            }}
                        />
                    )}
                </InputRow>
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
